import { useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Card, ErrorBox, Loading, Empty } from './ui'
import { usePnlOrders, usePnlClients } from './queries'
import { money, num, negClass, dayLabel, isNum } from './format'

const PAGE_SIZE = 50

const BASIS = {
  own_account: ['own account', 'Client ships on their own carrier account: no cost to us'],
  auspost_api: ['AusPost API', 'Actual eParcel cost from the AusPost API (ex-GST)'],
  gst_removed: ['÷1.1', 'Domestic AusPost/StarTrack label cost with GST removed'],
  as_recorded: ['as recorded', 'Carrier cost as recorded on the label'],
}

const th = 'text-right font-semibold text-xs text-slate-500 px-2.5 py-2 whitespace-nowrap'
const td = 'text-right px-2.5 py-1.5 tabular-nums whitespace-nowrap'

/** date / setDate are owned by the page so the choice survives tab switches. */
export default function OrdersTab({ date, setDate, maxDate }) {
  const [clientId, setClientId] = useState('')
  const [lossOnly, setLossOnly] = useState(false)
  const [page, setPage] = useState(1)

  useEffect(() => { setPage(1) }, [date, clientId, lossOnly])

  // Clients that shipped that day, for the filter.
  const clientsQ = usePnlClients(date, date, !!date)
  const params = { date, client_id: clientId || undefined, loss_only: lossOnly || undefined, page, page_size: PAGE_SIZE }
  const { data, error, isLoading, isFetching } = usePnlOrders(params, !!date)

  const total = Number(data?.total) || 0
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE))
  const orders = data?.orders || []

  return (
    <Card title="Profit per order">
      <div className="flex flex-wrap gap-2 items-center mb-3">
        <input
          type="date"
          className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-[13px] bg-white"
          value={date || ''}
          max={maxDate}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          aria-label="Ship date"
        />
        <select
          className="border border-slate-300 rounded-lg px-2.5 py-1.5 text-[13px] bg-white max-w-[220px]"
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          aria-label="Client"
        >
          <option value="">All clients</option>
          {(clientsQ.data?.clients || []).map((c) => (
            <option key={c.client_id} value={c.client_id}>{c.name || c.client_id}</option>
          ))}
        </select>
        <label className="text-[12.5px] text-slate-700 inline-flex gap-1.5 items-center cursor-pointer">
          <input type="checkbox" checked={lossOnly} onChange={(e) => setLossOnly(e.target.checked)} />
          Loss-making only
        </label>
        {isFetching && <span className="text-xs text-slate-400">updating…</span>}
      </div>

      {error ? (
        <ErrorBox error={error} what="orders" />
      ) : isLoading && !data ? (
        <Loading />
      ) : (
        <>
          <div className="text-[12.5px] text-slate-500 mb-3 leading-relaxed">
            {num(total)} {lossOnly ? 'loss-making ' : ''}orders shipped on {dayLabel(data?.date || date)}
            {clientId ? ' for this client' : ''}. Each one carries the same share of that day&apos;s costs:{' '}
            <b className="text-slate-700">
              {money(data?.labour_per_order, 2)} labour + {money(data?.overhead_per_order, 2)} overheads ={' '}
              {money(data?.cost_share_per_order, 2)}
            </b>.
          </div>
          {!orders.length ? (
            <Empty>No orders shipped on this day{clientId || lossOnly ? ' match the filters' : ''}.</Empty>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className="border-b border-slate-300">
                    <th className={`${th} text-left`}>Order</th>
                    <th className={`${th} text-left`}>Client</th>
                    <th className={th}>Items</th>
                    <th className={`${th} text-left`}>Service</th>
                    <th className={th}>Fees</th>
                    <th className={th}>Shipping charged</th>
                    <th className={th}>Postage (ex-GST)</th>
                    <th className={th}>Share of day&apos;s costs</th>
                    <th className={th}>Profit</th>
                  </tr>
                </thead>
                <tbody>
                  {orders.map((o) => {
                    const basis = BASIS[o.cost_basis]
                    return (
                      <tr key={o.order_id} className="border-b border-slate-100">
                        <td className={`${td} text-left`}>{o.order_number || o.order_id}</td>
                        <td className={`${td} text-left max-w-[180px] truncate`} title={o.client_name}>{o.client_name || '—'}</td>
                        <td className={td}>{num(o.items)}</td>
                        <td className={`${td} text-left`}>{[o.carrier, o.service].filter(Boolean).join(' · ') || '—'}</td>
                        <td className={td}>{money(o.fees, 2)}</td>
                        <td className={td}>{money(o.shipping, 2)}</td>
                        <td className={td}>
                          {money(o.carrier_cost, 2)}
                          {basis && (
                            <span title={basis[1]} className="ml-1.5 text-[10px] px-1.5 py-px rounded-full bg-slate-100 text-slate-500">
                              {basis[0]}
                            </span>
                          )}
                        </td>
                        <td className={td}>{money(o.cost_share, 2)}</td>
                        <td className={`${td} font-semibold ${negClass(o.profit)}`}>{money(o.profit, 2)}</td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
          {pages > 1 && (
            <div className="flex items-center justify-end gap-2 mt-3 text-[13px]">
              <button type="button" className="btn-secondary !px-2 !py-1" disabled={page <= 1} onClick={() => setPage(page - 1)} aria-label="Previous page">
                <ChevronLeft size={14} />
              </button>
              <span className="text-slate-600">Page {page} of {pages}</span>
              <button type="button" className="btn-secondary !px-2 !py-1" disabled={page >= pages} onClick={() => setPage(page + 1)} aria-label="Next page">
                <ChevronRight size={14} />
              </button>
            </div>
          )}
          {orders.length > 0 && isNum(data?.cost_share_per_order) && (
            <p className="text-xs text-slate-500 mt-2 leading-relaxed">
              <b>Worth knowing:</b> storage and other monthly charges are paid per client, not per order, so they
              show on the Clients tab, not here. Orders on quiet days carry a bigger share of costs than orders on busy days.
            </p>
          )}
        </>
      )}
    </Card>
  )
}
